import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ThemeProvider } from './components/ThemeProvider';
import CustomCursor from './components/CustomCursor';
import Nav from './components/Nav';
import Footer from './components/footer';
import Home from './pages/home';
import About from "./pages/about";
import Services from "./pages/services";
import Contact from "./pages/contact";
import Project from './pages/project';
import { FiArrowUp } from 'react-icons/fi';

// Scroll progress bar indicator
const ScrollProgressBar = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalScrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScrollHeight > 0) {
        const scrolled = (window.scrollY / totalScrollHeight) * 100;
        setScrollProgress(scrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 z-[60] origin-left"
      style={{ scaleX: scrollProgress / 100 }}
    />
  );
};

// Sleek app loader screen
const LoadingScreen = ({ onFinished }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onFinished, 500); // Allow fade-out animation to complete
    }, 1500);
    return () => clearTimeout(timer);
  }, [onFinished]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 bg-[#080808] z-[9999] flex flex-col items-center justify-center text-white"
        >
          <div className="flex flex-col items-center gap-4">
            {/* Sleek rotating ring loader */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-purple-500 border-l-transparent"
            />
            <motion.h1
              initial={{ letterSpacing: "0.2em", opacity: 0.7 }}
              animate={{ letterSpacing: "0.4em", opacity: 1 }}
              transition={{ duration: 1.2, repeat: Infinity, repeatType: "reverse" }}
              className="text-lg font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 font-sans uppercase mt-4"
            >
              Softkel
            </motion.h1>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Back to Top button
const BackToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-white dark:bg-[#111111] hover:bg-slate-100 dark:hover:bg-neutral-800 border border-slate-200/60 dark:border-neutral-800/60 text-slate-700 dark:text-slate-300 shadow-xl z-40 transition-all hover:scale-105 active:scale-95"
          aria-label="Back to Top"
        >
          <FiArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// Page Transition wrapper
const PageWrapper = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="flex-grow w-full flex flex-col"
    >
      {children}
    </motion.div>
  );
};

// Routes Coordinator
const AppContent = () => {
  const location = useLocation();

  // Scroll to top on pathname changes (for route page shifts)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen relative bg-white dark:bg-[#080808] transition-colors duration-500">
      <Nav />
      <ScrollProgressBar />
      
      <main className="flex-grow flex flex-col w-full">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
            <Route path="/services" element={<PageWrapper><Services /></PageWrapper>} />
            <Route path="/contactus" element={<PageWrapper><Contact /></PageWrapper>} />
            <Route path="/ourwork" element={<PageWrapper><Project /></PageWrapper>} />
          </Routes>
        </AnimatePresence>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

function App() {
  const [appLoading, setAppLoading] = useState(true);

  return (
    <ThemeProvider>
      <CustomCursor />
      <LoadingScreen onFinished={() => setAppLoading(false)} />
      
      {!appLoading && (
        <Router>
          <AppContent />
        </Router>
      )}
    </ThemeProvider>
  );
}

export default App;
